/**
 * Admin: push notifications. Which alerts exist and how they read (templates, thresholds, audience,
 * cooldown), the global rules (kill switch, quiet hours, daily caps), a test of any alert on the
 * admin's own devices, delivery stats, and campaigns (news and offers to a segment, now or scheduled).
 * The CRM edits the same settings and campaigns (GET/PUT /api/crm/settings/push, /api/crm/push/*).
 * Agent messages (the approved list staff send from) are at the bottom (AdminAgentMessages.tsx).
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  CAMPAIGN_AUDIENCES,
  fmtDateTime,
  parsePushSettings,
  rangeQuery,
  todayIn,
  PUSH_CATEGORIES,
  PUSH_GROUPS,
  type CampaignAudience,
  type PushCampaign,
  type PushCategoryDef,
  type PushCategoryKey,
  type PushCategorySettings,
  type PushSettings,
} from '@platform/shared';
import { DateField, isCustom, periodText, TimeFrameSelect, type Period } from '../components/DateRange';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { enablePush, usePushState } from '../lib/push';
import { AgentMessagesSection } from './AdminAgentMessages';

const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const area = 'min-h-[64px] w-full rounded-lg border border-line bg-bg px-3 py-2 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

interface View {
  settings: PushSettings;
  defaults: PushSettings;
  updatedBy: string | null;
  updatedAt: string | null;
  available: boolean;
}
interface Stats {
  days: number;
  categories: { category: string; sent: number; clicked: number; failed: number; dropped: number }[];
  devices: { devices: number; users: number; ios: number; android: number; desktop: number };
}

/** Delivery stats periods; the server keeps 90 days. */
const STATS_PERIODS: { id: number; label: string }[] = [
  { id: 1, label: '24 hours' },
  { id: 7, label: '7 days' },
  { id: 30, label: '30 days' },
  { id: 90, label: '90 days' },
];

function Section({ title, sub, children, right }: { title: string; sub?: ReactNode; children: ReactNode; right?: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="m-0 text-sm font-semibold">{title}</h2>
          {sub && <p className="m-0 mt-0.5 text-xs text-muted">{sub}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Check({ label, checked, onChange, disabled, hint }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; hint?: string }) {
  return (
    <label className={cx('flex items-center gap-2 text-xs', disabled && 'opacity-50')} title={hint}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function Labeled({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={cx('flex min-w-0 flex-col gap-1 text-[11px] text-muted', className)}>
      {label}
      {children}
    </label>
  );
}

export function PushTab() {
  const [view, setView] = useState<View | null>(null);
  const [form, setForm] = useState<PushSettings | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [open, setOpen] = useState<PushCategoryKey | null>(null);
  const push = usePushState();

  const reload = useCallback(async () => {
    try {
      const v = await api.get<View>('/api/admin/push/settings');
      setView(v);
      setForm(structuredClone(v.settings));
      setLoadErr(null);
    } catch (e) {
      setLoadErr(err(e));
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  const [statsPeriod, setStatsPeriod] = useState<Period<number>>({ id: 7 });
  const statsQs = isCustom(statsPeriod) ? rangeQuery(statsPeriod.range, 'utc') : `days=${statsPeriod.id}`;
  useEffect(() => {
    let live = true;
    api.get<Stats>(`/api/admin/push/stats?${statsQs}`).then(
      (st) => live && setStats(st),
      (e) => live && setLoadErr(err(e)),
    );
    return () => {
      live = false;
    };
  }, [statsQs]);
  if (!view || !form) return loadErr ? <p className="text-sm text-down">{loadErr}</p> : <Spinner />;

  const edit = (fn: (f: PushSettings) => void) => {
    setMsg(null);
    setForm((cur) => {
      const next = structuredClone(cur!);
      fn(next);
      return next;
    });
  };
  const setCat = (k: PushCategoryKey, patch: Partial<PushCategorySettings>) => edit((f) => Object.assign(f.categories[k], patch));
  const parsed = parsePushSettings(form);
  const dirty = JSON.stringify(form) !== JSON.stringify(view.settings);

  const save = async () => {
    if (!parsed.ok) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.put('/api/admin/push/settings', { settings: parsed.settings });
      setMsg({ ok: true, text: 'Saved. New alerts use these settings within a few seconds.' });
      await reload();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };
  const test = async (category: PushCategoryKey) => {
    setMsg(null);
    try {
      const r = await api.post<{ devices: number; title: string }>('/api/admin/push/test', { category });
      setMsg({
        ok: r.devices > 0,
        text: r.devices > 0 ? `Test “${r.title}” sent to ${r.devices} of your devices (saved settings).` : 'You have no device with push on. Turn on notifications on this device first.',
      });
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    }
  };

  const statOf = (k: string) => stats?.categories.find((c) => c.category === k);

  return (
    <div className="flex max-w-[1100px] flex-col gap-4">
      {!view.available && (
        <p className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-xs text-warn">
          Push is off on this server: set VAPID_PUBLIC_KEY and VAPID_PRIVATE_KEY in the API’s .env (npx web-push generate-vapid-keys) and restart. Until then
          alerts appear in the bell only.
        </p>
      )}

      <Section
        title={`Delivery, ${isCustom(statsPeriod) ? periodText(statsPeriod, STATS_PERIODS) : `last ${periodText(statsPeriod, STATS_PERIODS)}`}`}
        sub={stats ? `${stats.devices.devices} devices for ${stats.devices.users} clients · iPhone/iPad ${stats.devices.ios} · Android ${stats.devices.android} · computers ${stats.devices.desktop}` : undefined}
        right={
          <span className="flex items-center gap-2">
            <TimeFrameSelect label="Delivery period" zone="utc" maxDays={90} presets={STATS_PERIODS} value={statsPeriod} onChange={setStatsPeriod} />
            {push.support === 'supported' && push.permission !== 'granted' && (
              <button type="button" className={btn} onClick={() => void enablePush()}>
                Turn on push on this device
              </button>
            )}
          </span>
        }
      >
        {stats && stats.categories.length ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="text-left text-muted">
                  {['Alert', 'Sent', 'Opened', 'Open rate', 'Failed', 'Held back (caps, quiet hours, stale)'].map((h) => (
                    <th key={h} className="border-b border-line px-2 py-1.5 font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.categories.map((c) => (
                  <tr key={c.category}>
                    <td className="border-b border-line px-2 py-1.5">{PUSH_CATEGORIES.find((d) => d.key === c.category)?.label ?? c.category}</td>
                    <td className="num border-b border-line px-2 py-1.5">{c.sent}</td>
                    <td className="num border-b border-line px-2 py-1.5">{c.clicked}</td>
                    <td className="num border-b border-line px-2 py-1.5">{c.sent ? `${Math.round((c.clicked / c.sent) * 100)}%` : '—'}</td>
                    <td className="num border-b border-line px-2 py-1.5">{c.failed}</td>
                    <td className="num border-b border-line px-2 py-1.5">{c.dropped}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="m-0 text-xs text-muted">Nothing sent yet.</p>
        )}
      </Section>

      <Section title="Rules for every push" sub="Mandatory alerts (margin, stop-out, verification, new sign-in) ignore the pause, quiet hours and caps.">
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Check label="Push on (off pauses everything except mandatory alerts)" checked={form.enabled} onChange={(v) => edit((f) => void (f.enabled = v))} />
          <Check label="Show amounts in notifications (P&L, deposits)" checked={form.showAmounts} onChange={(v) => edit((f) => void (f.showAmounts = v))} />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Labeled label="Quiet hours">
            <Check label="On (client’s time zone)" checked={form.quietHours.enabled} onChange={(v) => edit((f) => void (f.quietHours.enabled = v))} />
          </Labeled>
          <Labeled label="From">
            <input type="time" value={form.quietHours.start} onChange={(e) => edit((f) => void (f.quietHours.start = e.target.value))} className={input} />
          </Labeled>
          <Labeled label="Until">
            <input type="time" value={form.quietHours.end} onChange={(e) => edit((f) => void (f.quietHours.end = e.target.value))} className={input} />
          </Labeled>
          <Labeled label="Pushes per client per 24 h">
            <input inputMode="numeric" value={form.dailyCap} onChange={(e) => edit((f) => void (f.dailyCap = Number(e.target.value.replace(/\D/g, '')) || 0))} className={cx(input, 'num')} />
          </Labeled>
          <Labeled label="Marketing pushes per 24 h">
            <input inputMode="numeric" value={form.marketingDailyCap} onChange={(e) => edit((f) => void (f.marketingDailyCap = Number(e.target.value.replace(/\D/g, '')) || 0))} className={cx(input, 'num')} />
          </Labeled>
        </div>
      </Section>

      <Section title="Alerts" sub="Turn alerts on or off, edit their wording and thresholds. Placeholders in {braces} are filled for each client.">
        {PUSH_GROUPS.map((g) => (
          <div key={g.id} className="flex flex-col">
            <h3 className="m-0 mb-1 text-[11px] font-semibold tracking-wide text-muted uppercase">{g.label}</h3>
            {PUSH_CATEGORIES.filter((d) => d.group === g.id).map((d) => (
              <CategoryRow
                key={d.key}
                def={d}
                value={form.categories[d.key]}
                defaults={view.defaults.categories[d.key]}
                open={open === d.key}
                onToggle={() => setOpen(open === d.key ? null : d.key)}
                onChange={(patch) => setCat(d.key, patch)}
                onTest={() => void test(d.key)}
                stat={statOf(d.key)}
              />
            ))}
          </div>
        ))}
      </Section>

      <div className="sticky bottom-0 flex flex-wrap items-center gap-3 border-t border-line bg-bg py-3">
        <button type="button" className={primary} disabled={!dirty || !parsed.ok || busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save settings'}
        </button>
        <button type="button" className={btn} disabled={!dirty || busy} onClick={() => setForm(structuredClone(view.settings))}>
          Discard changes
        </button>
        {!parsed.ok && <span className="text-xs text-down">{parsed.error}</span>}
        {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
        {view.updatedAt && <span className="ml-auto text-[11px] text-muted">Last saved {fmtDateTime(view.updatedAt)} by {view.updatedBy}</span>}
      </div>

      <Campaigns />

      <AgentMessagesSection />
    </div>
  );
}

function CategoryRow({
  def,
  value,
  defaults,
  open,
  onToggle,
  onChange,
  onTest,
  stat,
}: {
  def: PushCategoryDef;
  value: PushCategorySettings;
  defaults: PushCategorySettings;
  open: boolean;
  onToggle: () => void;
  onChange: (p: Partial<PushCategorySettings>) => void;
  onTest: () => void;
  stat?: { sent: number; clicked: number };
}) {
  return (
    <div className="border-b border-line py-2 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <button type="button" onClick={onToggle} aria-expanded={open} className="min-w-[220px] flex-1 bg-transparent text-left text-fg">
          <span className="text-[13px] font-semibold">{def.label}</span>
          {def.mandatory && <span className="ml-2 rounded bg-raised px-1.5 py-px text-[10px] font-semibold text-muted">MANDATORY</span>}
          {def.marketing && <span className="ml-2 rounded bg-raised px-1.5 py-px text-[10px] font-semibold text-muted">MARKETING</span>}
          {def.staffManaged && (
            <span title="Not in the client's settings: staff turn it off per client in the CRM" className="ml-2 rounded bg-raised px-1.5 py-px text-[10px] font-semibold text-muted">
              STAFF ONLY
            </span>
          )}
          <span className="block text-[11px] text-muted">{def.description}</span>
        </button>
        {stat && <span className="num text-[11px] text-muted">{stat.sent} sent · {stat.clicked} opened</span>}
        <Check label={def.templated ? 'Alert on' : 'Push on'} checked={def.templated ? value.enabled : value.enabled && value.push} onChange={(v) => onChange(def.templated ? { enabled: v } : { enabled: v, push: v })} />
        {def.templated && <Check label="Push" checked={value.push} disabled={!value.enabled} onChange={(v) => onChange({ push: v })} />}
        <Check
          label="On for new clients"
          checked={def.mandatory || value.defaultOn}
          disabled={def.mandatory}
          onChange={(v) => onChange({ defaultOn: v })}
          hint="Clients who never changed their choice get it pushed"
        />
        <button type="button" className={btn} onClick={onToggle}>
          {open ? 'Close' : 'Edit'}
        </button>
      </div>
      {open && (
        <div className="mt-2 grid grid-cols-1 gap-3 rounded-lg bg-raised p-3 sm:grid-cols-2">
          {def.templated && (
            <>
              <Labeled label="Title" className="sm:col-span-2">
                <input value={value.title} maxLength={120} onChange={(e) => onChange({ title: e.target.value })} className={input} />
              </Labeled>
              <Labeled label="Text" className="sm:col-span-2">
                <textarea value={value.body} maxLength={300} onChange={(e) => onChange({ body: e.target.value })} className={area} />
              </Labeled>
              <Labeled label="Opens (app path or https URL)">
                <input value={value.link} onChange={(e) => onChange({ link: e.target.value })} className={input} />
              </Labeled>
              <p className="m-0 self-end text-[11px] text-muted">Placeholders: {def.placeholders.map((p) => `{${p}}`).join(' ')}</p>
            </>
          )}
          {!def.templated && <p className="m-0 text-[11px] text-muted sm:col-span-2">The wording comes from the event itself (it is the same text the client sees under the bell).</p>}
          {def.thresholds.map((t) => (
            <Labeled key={t.key} label={t.label}>
              <input value={value.thresholds[t.key] ?? ''} onChange={(e) => onChange({ thresholds: { ...value.thresholds, [t.key]: e.target.value } })} className={cx(input, 'num')} placeholder={defaults.thresholds[t.key]} />
              {t.hint && <span>{t.hint}</span>}
            </Labeled>
          ))}
          <Labeled label="Audience (trade and market alerts)">
            <select value={value.audience} onChange={(e) => onChange({ audience: e.target.value as PushCategorySettings['audience'] })} className={input}>
              <option value="both">Live and demo accounts</option>
              <option value="live">Live accounts only</option>
              <option value="demo">Demo accounts only</option>
            </select>
          </Labeled>
          <Labeled label="Minimum minutes between two of these per client (0 = no limit)">
            <input inputMode="numeric" value={value.cooldownMin} onChange={(e) => onChange({ cooldownMin: Number(e.target.value.replace(/\D/g, '')) || 0 })} className={cx(input, 'num')} />
          </Labeled>
          <div className="flex items-center gap-2 sm:col-span-2">
            <button type="button" className={btn} onClick={onTest}>
              Send me a test
            </button>
            {def.templated && (
              <button type="button" className={btn} onClick={() => onChange({ title: defaults.title, body: defaults.body, link: defaults.link, thresholds: { ...defaults.thresholds } })}>
                Reset wording to default
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- campaigns ----------------

interface CampaignForm {
  title: string;
  body: string;
  link: string;
  audience: CampaignAudience;
  days: string;
  symbol: string;
  users: string;
  countries: string;
  when: 'now' | 'later';
  at: string;
}
const EMPTY: CampaignForm = { title: '', body: '', link: '', audience: 'all', days: '7', symbol: '', users: '', countries: '', when: 'now', at: '' };

function campaignBody(f: CampaignForm) {
  const segment: Record<string, unknown> = { audience: f.audience };
  if (f.audience === 'inactive') segment.days = Number(f.days) || 0;
  if (f.audience === 'holding') segment.symbol = f.symbol.trim();
  if (f.audience === 'users') segment.users = f.users.split(/[\s,;]+/).filter(Boolean);
  const countries = f.countries.split(/[\s,;]+/).filter(Boolean);
  if (countries.length) segment.countries = countries;
  return { title: f.title, body: f.body, link: f.link || null, segment, scheduledAt: f.when === 'later' && f.at ? new Date(f.at).toISOString() : null };
}

const STATUS_TONE: Record<string, string> = { scheduled: 'text-warn', sending: 'text-accent', sent: 'text-up', cancelled: 'text-muted' };

function Campaigns() {
  const [list, setList] = useState<PushCampaign[] | null>(null);
  const [f, setF] = useState<CampaignForm>(EMPTY);
  const [estimate, setEstimate] = useState<{ clients: number; withPush: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = useCallback(() => {
    api
      .get<{ data: PushCampaign[] }>('/api/admin/push/campaigns')
      .then((r) => setList(r.data))
      .catch(() => setList([]));
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [load]);
  const set = (patch: Partial<CampaignForm>) => {
    setMsg(null);
    setEstimate(null);
    setF((cur) => ({ ...cur, ...patch }));
  };

  const doEstimate = async () => {
    try {
      setEstimate(await api.post<{ clients: number; withPush: number }>('/api/admin/push/estimate', { segment: campaignBody(f).segment }));
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    }
  };
  const send = async () => {
    const b = campaignBody(f);
    const verb = b.scheduledAt ? `schedule for ${fmtDateTime(b.scheduledAt)}` : 'send now';
    if (!window.confirm(`${verb[0]!.toUpperCase()}${verb.slice(1)}: “${f.title}”${estimate ? ` to ${estimate.clients} clients` : ''}?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.post('/api/admin/push/campaigns', b);
      setMsg({ ok: true, text: b.scheduledAt ? 'Scheduled.' : 'Sending. Clients get it within a minute (quiet hours and caps apply to the push).' });
      setF(EMPTY);
      setEstimate(null);
      load();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (id: string) => {
    if (!window.confirm('Cancel this campaign? Clients already notified keep it.')) return;
    await api.post(`/api/admin/push/campaigns/${id}/cancel`).catch((e) => setMsg({ ok: false, text: err(e) }));
    load();
  };

  return (
    <Section
      title="Campaigns: news and offers"
      sub="Each client in the segment gets it under the bell; it is pushed to clients who keep “News and offers” on, within the marketing cap. {first_name} is filled in. Promotions must not promise profits; link the risk warning where relevant."
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Labeled label="Title" className="sm:col-span-2">
          <input value={f.title} maxLength={120} onChange={(e) => set({ title: e.target.value })} className={input} placeholder="Gold hits a record high" />
        </Labeled>
        <Labeled label="Text" className="sm:col-span-2">
          <textarea value={f.body} maxLength={300} onChange={(e) => set({ body: e.target.value })} className={area} placeholder="See what’s moving the market today." />
        </Labeled>
        <Labeled label="Opens (app path or https URL)">
          <input value={f.link} onChange={(e) => set({ link: e.target.value })} className={input} placeholder="/trade/XAUUSD" />
        </Labeled>
        <Labeled label="Who">
          <select value={f.audience} onChange={(e) => set({ audience: e.target.value as CampaignAudience })} className={input}>
            {CAMPAIGN_AUDIENCES.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        </Labeled>
        {f.audience === 'inactive' && (
          <Labeled label="Days without signing in">
            <input inputMode="numeric" value={f.days} onChange={(e) => set({ days: e.target.value.replace(/\D/g, '') })} className={cx(input, 'num')} />
          </Labeled>
        )}
        {f.audience === 'holding' && (
          <Labeled label="Instrument symbol">
            <input value={f.symbol} onChange={(e) => set({ symbol: e.target.value.toUpperCase() })} className={input} placeholder="BTC" />
          </Labeled>
        )}
        {f.audience === 'users' && (
          <Labeled label="Client ids or emails (comma or new line)" className="sm:col-span-2">
            <textarea value={f.users} onChange={(e) => set({ users: e.target.value })} className={area} />
          </Labeled>
        )}
        <Labeled label="Only these countries (optional, e.g. GB, DE)">
          <input value={f.countries} onChange={(e) => set({ countries: e.target.value.toUpperCase() })} className={input} />
        </Labeled>
        <Labeled label="When">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-fg">
              <input type="radio" checked={f.when === 'now'} onChange={() => set({ when: 'now' })} /> Now
            </label>
            <label className="flex items-center gap-1.5 text-xs text-fg">
              <input type="radio" checked={f.when === 'later'} onChange={() => set({ when: 'later' })} /> Later
            </label>
            {f.when === 'later' && <DateField withTime aria-label="Send at" min={todayIn()} value={f.at} onChange={(at) => set({ at })} className={input} />}
          </div>
        </Labeled>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={btn} onClick={() => void doEstimate()}>
          Estimate reach
        </button>
        {estimate && (
          <span className="text-xs text-muted">
            {estimate.clients} clients · {estimate.withPush} with push on
          </span>
        )}
        <button type="button" className={primary} disabled={busy || !f.title.trim() || !f.body.trim() || (f.when === 'later' && !f.at)} onClick={() => void send()}>
          {f.when === 'later' ? 'Schedule' : 'Send now'}
        </button>
        {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
      </div>

      {list && list.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="text-left text-muted">
                {['Campaign', 'Who', 'When', 'Status', 'Clients', 'Pushed', 'Opened', ''].map((h) => (
                  <th key={h} className="border-b border-line px-2 py-1.5 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td className="max-w-[280px] border-b border-line px-2 py-1.5">
                    <span className="block truncate font-semibold">{c.title}</span>
                    <span className="block truncate text-muted">{c.body}</span>
                  </td>
                  <td className="border-b border-line px-2 py-1.5">
                    {CAMPAIGN_AUDIENCES.find((a) => a.id === c.segment.audience)?.label ?? c.segment.audience}
                    {c.segment.days ? ` (${c.segment.days} d)` : ''}
                    {c.segment.symbol ? ` (${c.segment.symbol})` : ''}
                    {c.segment.users ? ` (${c.segment.users.length})` : ''}
                    {c.segment.countries?.length ? ` · ${c.segment.countries.join(', ')}` : ''}
                  </td>
                  <td className="border-b border-line px-2 py-1.5 whitespace-nowrap">{fmtDateTime(c.sentAt ?? c.scheduledAt)}</td>
                  <td className={cx('border-b border-line px-2 py-1.5 font-semibold', STATUS_TONE[c.status])}>{c.status}</td>
                  <td className="num border-b border-line px-2 py-1.5">{c.targeted}</td>
                  <td className="num border-b border-line px-2 py-1.5">{c.pushed}</td>
                  <td className="num border-b border-line px-2 py-1.5">{c.clicked}</td>
                  <td className="border-b border-line px-2 py-1.5">
                    {(c.status === 'scheduled' || c.status === 'sending') && (
                      <button type="button" className={btn} onClick={() => void cancel(c.id)}>
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
